import { doGET, doPATCH, doPOST } from '../../utils/HttpUtils';
import { create } from 'zustand';
import { showError, showSuccess } from '../toaster';
import {
    ExternalCatalogOption,
    ExternalBookingLegType,
    ExternalBookingPaymentInfo,
    ExternalBookingQueryParams,
    ExternalBookingReportInfo,
    ExternalBookingState,
    IExternalBooking
} from '../../types/bookings';
import { downloadCsvFile } from '../../utils/export';
import { api } from '../api/apiHandler';
import { STORAGE_KEYS, getValue } from '../Storage/index';

const BASE = '/external-bookings';
const DEFAULT_INTEGRATION = 'labstack';

function unwrap<T>(res: any): T | null {
    if (res?.error || res?.status >= 400) return null;
    const payload = res?.data?.data ?? res?.data;
    return payload ?? null;
}

const buildCleanObject = <T extends Record<string, any>>(entries: Array<[string, any]>) =>
    entries.reduce((acc, [key, value]) => {
        (acc as Record<string, any>)[key] = value;
        return acc;
    }, {} as T);

const toRelativeApiPath = (value: string) => {
    if (!value) return value;

    try {
        const parsedUrl = new URL(value);
        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch (error) {
        return value;
    }
};

export const buildExternalBookingReportUrl = (value: string) => {
    if (!value) return value;
    if (/^https?:\/\//i.test(value)) return value;

    try {
        const baseOrigin = new URL(api.defaults.baseURL || '').origin;
        return `${baseOrigin}${value.startsWith('/') ? value : `/${value}`}`;
    } catch (error) {
        return value;
    }
};

export async function openExternalBookingReport(value: string) {
    const reportPath = toRelativeApiPath(value);
    if (!reportPath.startsWith('/api/v1/external-bookings/bookings/') ||
        !reportPath.includes('/reports/')) {
        window.open(buildExternalBookingReportUrl(value), '_blank', 'noopener,noreferrer');
        return;
    }

    const preview = window.open('', '_blank');
    try {
        const response = await api.get(reportPath.replace(/^\/api\/v1\//, ''), {
            responseType: 'blob',
            headers: {
                Authorization: `Bearer ${getValue(STORAGE_KEYS.TOKEN)}`,
                'x-api-key': 'web',
            },
        });
        const objectUrl = URL.createObjectURL(response.data);
        if (preview) preview.location.href = objectUrl;
        else {
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = decodeURIComponent(reportPath.split('/').pop() || 'report.pdf');
            link.click();
        }
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) {
        preview?.close();
        throw error;
    }
}

/** Provider types for external specialist booking (backend returns string[], e.g. DOCTOR). */
export async function fetchExternalProviderTypes(appointmentType: string = 'ONLINE', pincode?: string) {
    const q = new URLSearchParams({ integration: DEFAULT_INTEGRATION, appointmentType });
    if (pincode?.trim()) q.set('pincode', pincode.trim());
    const res = await doGET(`${BASE}/provider-types?${q.toString()}`);
    const raw = unwrap<string[] | unknown>(res);
    const arr = Array.isArray(raw) ? raw : [];
    return arr.map((p: any) =>
        typeof p === 'string'
            ? { providerType: p, name: p }
            : { providerType: p.providerType ?? p.id ?? '', name: p.name ?? p.providerType }
    );
}

export async function fetchExternalSpecialists(params: {
    providerType: string;
    appointmentType?: string;
    pincode?: string;
    latitude?: string;
    longitude?: string;
}) {
    const q = new URLSearchParams({
        integration: DEFAULT_INTEGRATION,
        providerType: params.providerType,
        appointmentType: params.appointmentType || 'ONLINE',
    });
    if (params.pincode?.trim()) q.set('pincode', params.pincode.trim());
    if (params.latitude?.trim()) q.set('latitude', params.latitude.trim());
    if (params.longitude?.trim()) q.set('longitude', params.longitude.trim());
    const res = await doGET(`${BASE}/specialists?${q.toString()}`);
    return (unwrap<any[]>(res) || []) as Array<{ id: string; name: string }>;
}

export async function fetchExternalLabs(pincode?: string, city?: string, search?: string) {
    const q = new URLSearchParams({ integration: DEFAULT_INTEGRATION });
    if (pincode?.trim()) q.set('pincode', pincode.trim());
    if (city?.trim()) q.set('city', city.trim());
    if (search?.trim()) q.set('search', search.trim());
    const res = await doGET(`${BASE}/labs?${q.toString()}`);
    return (unwrap<any[]>(res) || []) as Array<{ id: string; name: string }>;
}

export async function fetchExternalPackages(
    latitude?: number,
    longitude?: number,
    integration: string = DEFAULT_INTEGRATION,
    campId?: string
) {
    const q = new URLSearchParams({ integration });
    if (latitude != null && longitude != null) {
        q.set('latitude', String(latitude));
        q.set('longitude', String(longitude));
    }
    if (campId?.trim()) q.set('campId', campId.trim());
    const res = await doGET(`${BASE}/packages?${q.toString()}`);
    const raw = unwrap<any[]>(res) || [];
    return raw.map((item: any): ExternalCatalogOption => ({
        id: item?.id || '',
        name: item?.name || item?.packageName || item?.title || item?.id || '',
        price: typeof item?.price === 'number' ? item.price : undefined,
        customPrice: typeof item?.customPrice === 'number' ? item.customPrice : undefined,
        customMrp: typeof item?.customMrp === 'number' ? item.customMrp : undefined,
        markupPercentage: typeof item?.markupPercentage === 'number' ? item.markupPercentage : undefined,
        markupAmount: typeof item?.markupAmount === 'number' ? item.markupAmount : undefined,
        image: item?.image,
        coverImage: item?.coverImage,
        pinned: typeof item?.pinned === 'boolean' ? item.pinned : undefined,
        gender: item?.gender === 'male' || item?.gender === 'female' ? item.gender : undefined,
        campId: typeof item?.campId === 'string' ? item.campId : undefined,
        campIds: Array.isArray(item?.campIds) ? item.campIds.filter((id: unknown) => typeof id === 'string') : undefined,
    }));
}

export async function fetchExternalTests(
    opts: { pincode?: string; labId?: string; latitude?: number; longitude?: number; search?: string; integration?: string }
) {
    const q = new URLSearchParams({ integration: opts.integration || DEFAULT_INTEGRATION });
    if (opts.pincode?.trim()) q.set('pincode', opts.pincode.trim());
    if (opts.labId?.trim()) q.set('id', opts.labId.trim());
    if (opts.search?.trim()) q.set('search', opts.search.trim());
    if (opts.latitude != null && opts.longitude != null) {
        q.set('latitude', String(opts.latitude));
        q.set('longitude', String(opts.longitude));
    }
    const res = await doGET(`${BASE}/tests?${q.toString()}`);
    const raw = unwrap<any[]>(res) || [];
    return raw.map((item: any): ExternalCatalogOption => ({
        id: item?.id || '',
        name: item?.name || item?.testName || item?.title || item?.id || '',
        price: typeof item?.price === 'number' ? item.price : undefined,
        customPrice: typeof item?.customPrice === 'number' ? item.customPrice : undefined,
        customMrp: typeof item?.customMrp === 'number' ? item.customMrp : undefined,
        image: item?.image,
        coverImage: item?.coverImage,
        pinned: typeof item?.pinned === 'boolean' ? item.pinned : undefined,
    }));
}

export async function fetchExternalBookingPaymentDetails(id: string) {
    const res = await doGET(`${BASE}/bookings/${id}/payment-details`);
    return unwrap<ExternalBookingPaymentInfo>(res);
}

export async function fetchExternalBookingById(id: string) {
    const res = await doGET(`${BASE}/bookings/${id}`);
    return unwrap<IExternalBooking>(res);
}

export async function fetchExternalBookingReports(id: string, refreshFromProvider = false) {
    const res = refreshFromProvider
        ? await doPOST(`${BASE}/bookings/${id}/reports/fetch`, {})
        : await doGET(`${BASE}/bookings/${id}/reports`);
    if (refreshFromProvider && (res?.error || res?.status >= 400)) {
        throw new Error(res?.message || 'Unable to fetch reports. Please try again.');
    }
    const payload = unwrap<ExternalBookingReportInfo>(res);

    if (!payload) {
        if (refreshFromProvider) throw new Error('No report response was received. Please try again.');
        return null;
    }

    return {
        ...payload,
        report: Array.isArray(payload.report)
            ? payload.report.map((reportUrl) => toRelativeApiPath(reportUrl))
            : [],
    };
}

export async function cancelExternalBooking(booking: IExternalBooking, reason: string) {
    const isDiagnostics = booking.providerType === 'DIAGNOSTICS' || booking.providerType === 'CAMP';
    const path = isDiagnostics
        ? `${BASE}/diagnostics/bookings/${booking._id}/cancel`
        : `${BASE}/bookings/${booking._id}/cancel`;
    const response = await doPOST(path, { reason });
    return unwrap<IExternalBooking>(response);
}

export async function rescheduleExternalBooking(
    booking: IExternalBooking,
    payload: { bookingDate: string; bookingTime: string; leg?: ExternalBookingLegType }
) {
    const isDiagnostics = booking.providerType === 'DIAGNOSTICS' || booking.providerType === 'CAMP';
    const path = isDiagnostics
        ? `${BASE}/diagnostics/bookings/${booking._id}/reschedule`
        : `${BASE}/bookings/${booking._id}/reschedule`;
    const response = await doPOST(path, isDiagnostics
        ? payload
        : {
            bookingDate: payload.bookingDate,
            selectedSlotTime: payload.bookingTime,
        }
    );
    const updated = unwrap<IExternalBooking>(response);
    if (!updated) {
        throw new Error(response?.message || 'Unable to reschedule this visit. Please try again.');
    }
    return updated;
}

export async function uploadExternalBookingDocuments(
    bookingId: string,
    type: 'report' | 'other' | 'fitness_certificate',
    files: File[],
    leg?: ExternalBookingLegType
) {
    const formData = new FormData();
    formData.append('type', type);
    if (leg) formData.append('leg', leg);
    files.forEach((file) => formData.append('files', file));
    const response = await doPOST(`${BASE}/bookings/${bookingId}/documents`, formData);
    return unwrap<{
        booking: IExternalBooking;
        documents: NonNullable<IExternalBooking['documents']>;
        report?: string[];
    }>(response);
}

export async function updateExternalBookingSkipReport(id: string, skipReport: boolean) {
    const response = await doPATCH(`${BASE}/bookings/${id}/skip-report`, { skipReport });
    return unwrap<IExternalBooking>(response);
}

export async function updateExternalBookingHealthCertificateRequired(
    id: string,
    skipHealthCertificate: boolean
) {
    const response = await doPATCH(`${BASE}/bookings/${id}/health-certificate-required`, { skipHealthCertificate });
    return unwrap<IExternalBooking>(response);
}

export async function updateFitnessCertificateVerification(
    id: string,
    isFitnessCertificateVerified: boolean
) {
    const response = await doPATCH(`${BASE}/bookings/${id}/fitness-certificate-verification`, {
        isFitnessCertificateVerified,
    });
    return unwrap<IExternalBooking>(response);
}

export async function openExternalBookingFitnessCertificate(bookingId: string, fileName: string) {
    const preview = window.open('', '_blank');
    try {
        const response = await api.get(
            `external-bookings/bookings/${encodeURIComponent(bookingId)}/fitness-certificates/${encodeURIComponent(fileName)}`,
            {
                responseType: 'blob',
                headers: {
                    Authorization: `Bearer ${getValue(STORAGE_KEYS.TOKEN)}`,
                    'x-api-key': 'web',
                },
            }
        );
        const objectUrl = URL.createObjectURL(response.data);
        if (preview) preview.location.href = objectUrl;
        else {
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = fileName;
            link.click();
        }
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) {
        preview?.close();
        throw error;
    }
}

const fetchExternalBookingPage = async (filters: ExternalBookingQueryParams, page: number, limit: number) => {
    const cleanFilters = buildCleanObject<Record<string, string>>(
        Object.entries(filters).filter(([_, value]) => value !== undefined && value !== '')
    );

    const queryParams = new URLSearchParams(cleanFilters as Record<string, string>);
    queryParams.set('page', String(page));
    queryParams.set('limit', String(limit));

    return doGET(`${BASE}/bookings?${queryParams.toString()}`);
};

const formatBookingDate = (value?: string) => {
    if (!value) return '';
    if (value.length === 8) {
        return `${value.substring(6, 8)}/${value.substring(4, 6)}/${value.substring(0, 4)}`;
    }
    return value;
};

const toLabelCase = (value?: string) => {
    if (!value) return '';
    return value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const deriveCorporateNameFromEmail = (email?: string) => {
    if (!email || !email.includes('@')) return '';

    const domain = email.split('@')[1] || '';
    const companySegment = domain.split('.')[0] || '';

    return companySegment
        ? companySegment.charAt(0).toUpperCase() + companySegment.slice(1)
        : '';
};

const buildCorporateNameMap = async () => {
    const response = await doGET('/corporates');
    const corporates = Array.isArray(response?.data?.data) ? response.data.data : [];

    return new Map<string, string>(
        corporates.map((corporate: any) => [
            corporate?._id || '',
            corporate?.companyName || corporate?.name || '',
        ])
    );
};

const externalBookingsStore = create<ExternalBookingState>((set, get) => ({
    data: [],
    totalPages: 0,
    total: 0,
    currentPage: 1,
    filters: {},
    isLoading: false,
    limit: 10,

    fetchGrid: async () => {
        const { filters, currentPage, limit, isLoading } = get();
        if (isLoading) return;
        set({ isLoading: true });
        try {
            const response = await fetchExternalBookingPage(filters, currentPage, limit);
            if (get().filters !== filters || get().currentPage !== currentPage) return;

            if (response.status >= 200 && response.status < 400) {
                const rows = (response.data?.data || []) as IExternalBooking[];
                const pagination = response.data?.pagination || {};
                const total = pagination.total ?? rows.length;
                const totalPages = pagination.totalPages ?? Math.ceil(total / limit);

                set({
                    data: rows,
                    total,
                    totalPages,
                    currentPage: Math.min(pagination.page ?? currentPage, Math.max(1, totalPages)),
                });
            } else {
                showError(response.message || 'Failed to fetch external bookings');
            }
        } catch (error) {
            if (get().filters === filters && get().currentPage === currentPage) showError('Failed to fetch external bookings');
        } finally {
            set({ isLoading: false });
            if (get().filters !== filters || get().currentPage !== currentPage) await get().fetchGrid();
        }
    },

    setFilters: (newFilters: ExternalBookingQueryParams) => {
        set({ filters: newFilters, currentPage: 1 });
        get().fetchGrid();
    },

    onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        const nextPage = page + 1;
        set({ currentPage: nextPage });
        get().fetchGrid();
    },

    exportBookings: async () => {
        try {
            const { filters } = get();
            const cleanFilters = buildCleanObject<Record<string, string>>(
                Object.entries(filters).filter(([_, value]) => value !== undefined && value !== '')
            );
            const queryParams = new URLSearchParams(cleanFilters as Record<string, string>);
            const response = await doGET(`${BASE}/bookings?${queryParams.toString()}`);
            if (!(response.status >= 200 && response.status < 400)) {
                showError(response.message || 'Failed to export external bookings');
                return false;
            }

            const bookings = (response.data?.data || []) as IExternalBooking[];

            if (!bookings.length) {
                showError('No external bookings found to export');
                return false;
            }

            const corporateNameMap = await buildCorporateNameMap();
            const bookingRows = await Promise.all(
                bookings.map(async (booking) => {
                    const paymentDetails = booking._id
                        ? await fetchExternalBookingPaymentDetails(booking._id)
                        : null;
                    const reportList = Array.isArray(booking.report) && booking.report.length
                        ? booking.report.map((reportUrl) => toRelativeApiPath(reportUrl))
                        : booking._id
                            ? (await fetchExternalBookingReports(booking._id))?.report || []
                            : [];
                    const couponUsed =
                        paymentDetails?.summary?.couponCode ||
                        paymentDetails?.payment?.couponCode ||
                        '';

                    return [
                        formatBookingDate(booking.bookingDate),
                        booking.bookingTime || '',
                        toLabelCase(booking.providerType),
                        toLabelCase(booking.appointmentType),
                        booking.doctorName || booking.meta?.doctorName || '',
                        booking.labName || booking.meta?.labName || '',
                        Array.isArray(booking.packageNames) ? booking.packageNames.join(', ') : '',
                        Array.isArray(booking.testNames) ? booking.testNames.join(', ') : '',
                        booking.patientName || '',
                        booking.patientEmail || '',
                        booking.patientPhone || '',
                        booking.address || booking.meta?.address || booking.meta?.addressLine || '',
                        booking.meta?.pincode || '',
                        couponUsed,
                        booking.corporateId || '',
                        corporateNameMap.get(booking.corporateId || '') ||
                            deriveCorporateNameFromEmail(booking.patientEmail) ||
                            '',
                        reportList.map((reportUrl) => buildExternalBookingReportUrl(reportUrl)).join(', '),
                        booking.externalBookingId || '',
                        toLabelCase(booking.packageType === 'HYBRID' ? booking.overallStatus || booking.status : booking.status),
                        toLabelCase(booking.paymentStatus),
                        booking.price ?? '',
                    ];
                })
            );

            downloadCsvFile(
                `external-bookings-export-${new Date().toISOString().slice(0, 10)}.csv`,
                [
                    'Booking Date',
                    'Booking Time',
                    'Provider Type',
                    'Appointment Type',
                    'Doctor Name',
                    'Lab Name',
                    'Package Name',
                    'Test Name',
                    'Patient Name',
                    'Patient Email',
                    'Patient Phone',
                    'Address',
                    'Pincode',
                    'Coupon Used',
                    'Corporate ID',
                    'Corporate Name',
                    'Reports',
                    'External Booking ID',
                    'Overall Status',
                    'Payment Status',
                    'Price',
                ],
                bookingRows
            );

            showSuccess('External bookings exported successfully');
            return true;
        } catch (error) {
            showError('Failed to export external bookings');
            return false;
        }
    },
}));

export const useExternalBookingStore = () => externalBookingsStore((state) => state);
